import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import Loader from "../../../components/Loader/Loader";
import DashboardStats from "./Components/DashboardStats";
import UpcomingBookings from "./Components/UpcomingBookings";
import GlobalChart from "./Components/GlobalChart/GlobalChart";
import SalesChart from "./Components/SalesChart/SalesChart";
import SalesStatesChart from "./Components/SalesStatesChart/SalesStatesChart";
import PieSalesChart from "./Components/SalesStatesChart/PieSalesChart";
import StaffCommisionChart from "./Components/StaffCommisionChart/StaffCommisionChart";
import StaffPieChart from "./Components/StaffCommisionChart/StaffPieChart";
import { getEvents } from "../../../actions/adminDashboardActions";
import { getStaffs } from "../../../actions/staffActions";
import HomepageToggle from "../../../components/HomepageToggle/HomepageToggle";
import AccountSetupNotice from "../../../components/AccountSetupNotice/AccountSetupNotice";
import useFeatureRedirect from "../../../hooks/useFeatureRedirect";
import { FEATURE_IDS } from "../../../constants/featureAccess";
import { hasMetricsReporting } from "../../../utils/featureAccessHelpers";
import "./Components/DashboardCss/dashboardAdmin.scss";
import PageHeader from "../../../components/PageHeader/PageHeader";
import "../../../components/HomepageToggle/homepageToggle.scss";

const Section = ({ title, hint, children }) => (
  <section className="dash-section">
    <div className="dash-section__head">
      <div>
        <h2 className="dash-section__title">{title}</h2>
        {hint ? <p className="dash-section__hint">{hint}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

const Dashboard = (props) => {
  const [events, setEvents] = useState([{ value: "", label: "All events" }]);
  const [staffs, setStaffs] = useState([{ value: "", label: "All staff" }]);

  useEffect(() => {
    const { getEvents, getStaffs } = props;
    getEvents({
      data: {},
      onSuccess: function (data) {
        setEvents([{ value: "", label: "All events" }, ...((data && data.events) || [])]);
      },
      onError: function (error) {
        console.log("error", error);
      },
    });

    getStaffs({
      data: {
        pageSize: 10000,
        pageNumber: 1,
      },
      onSuccess: function (response) {
        const staffList = ((response && response.entity) || []).map((staff) => ({
          label: `${staff.first_name || ""} ${staff.last_name || ""}`.trim(),
          value: staff.id,
        }));
        setStaffs([{ value: "", label: "All staff" }, ...staffList]);
      },
      onError: function (error) {
        console.log("error", error);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-dashboard">
      <PageHeader
        title="Analytics"
        subtitle="Revenue, bookings and team performance for your business."
        actions={<HomepageToggle />}
      />

      <AccountSetupNotice />

      <DashboardStats />

      <Section title="Bookings" hint="Volume by status and what's coming up next.">
        <div className="dash-grid dash-grid--main-side">
          <GlobalChart events={events} />
          <UpcomingBookings />
        </div>
      </Section>

      <Section title="Revenue" hint="Net sales after discounts. Cancelled and unpaid bookings are excluded.">
        <div className="dash-grid dash-grid--halves">
          <SalesChart />
          <SalesStatesChart events={events} />
        </div>
      </Section>

      <Section title="Capacity & team" hint="How much of your bookable time is used, and who is earning it.">
        <div className="dash-grid dash-grid--halves">
          <PieSalesChart events={events} />
          <StaffPieChart staffs={staffs} />
        </div>
        <div className="dash-grid dash-grid--full">
          <StaffCommisionChart staffs={staffs} />
        </div>
      </Section>
    </div>
  );
};

const ConnectedDashboard = connect(null, { getEvents, getStaffs })(Dashboard);

const DashboardGuarded = (props) => {
  const { features, loaded } = useSelector((state) => state.featureAccess);

  useFeatureRedirect(
    FEATURE_IDS.METRICS_REPORTING,
    "/admin/scheduled-events"
  );

  if (!loaded || !hasMetricsReporting(features)) {
    return <Loader isShowLoader />;
  }

  return <ConnectedDashboard {...props} />;
};

export default DashboardGuarded;
